// imports
import {
  Box,
  Button,
  ClickAwayListener,
  Collapse,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  styled,
} from '@mui/material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { useRef, useState } from 'react';
import { USWDS_NAV_BLUE, LINK_BLUE, TEXT_GRAY, FOCUS_BLUE, LIGHT_GRAY } from '../../constants/colors';

type DropdownMenuItemProps = {
  label: string;
  submenu: { label: string; link: string }[];
};

const StyledExpandMore = styled(ExpandMore)(() => ({
  height: '1rem',
  width: '1rem',
  marginTop: '0.2rem',
  marginLeft: '0.25rem',
  color: 'inherit',
}));

const StyledExpandLess = styled(ExpandLess)(() => ({
  height: '1rem',
  width: '1rem',
  marginTop: '0.2rem',
  marginLeft: '0.25rem',
  color: 'inherit',
}));

const DropdownMenuItem = ({ label, submenu }: DropdownMenuItemProps) => {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  const handleToggle = () => {
    setOpen((prev) => !prev);
  };

  const handleClickAway = (event: MouseEvent | TouchEvent) => {
    if (buttonRef.current?.contains(event.target as Node)) return;
    setOpen(false);
  };

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box sx={{ position: 'relative' }}>
        <Button
          ref={buttonRef}
          onClick={handleToggle}
          sx={{
            backgroundColor: open ? USWDS_NAV_BLUE : LIGHT_GRAY,
            borderRadius: 0,
            color: open ? '#ffffff' : TEXT_GRAY,
            fontSize: '1rem',
            fontWeight: 700,
            lineHeight: 0.9,
            height: '100%',
            px: '1rem',
            py: '1rem',
            textTransform: 'none',
            borderBottom: open ? 'none' : '1px solid #D1D4D5',
            '& .MuiButton-endIcon': {
              marginLeft: 0,
            },
            '&:focus': {
              outline: `0.25rem solid ${FOCUS_BLUE}`,
              outlineOffset: 0,
              zIndex: 1,
            },
            '&:hover': {
              '&::after': open
                ? {}
                : {
                    transform: 'scaleX(1)',
                    opacity: 1,
                  },
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              left: '1rem',
              right: '1rem',
              bottom: 0,
              height: '4px',
              backgroundColor: LINK_BLUE,
              opacity: 0,
            },
          }}
          endIcon={open ? <StyledExpandLess /> : <StyledExpandMore />}
        >
          {label}
        </Button>
        <Collapse
          in={open}
          timeout={0}
          unmountOnExit
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            bgcolor: USWDS_NAV_BLUE,
            boxShadow: 3,
            zIndex: 2400,
            width: 240,
          }}
        >
          <List disablePadding sx={{ p: '1rem' }}>
            {submenu.map((item, idx) => (
              <ListItem key={idx} disablePadding onClick={() => setOpen(false)} sx={{ mt: idx > 0 ? '.5rem' : 0 }}>
                <ListItemButton
                  component="a"
                  href={item.link}
                  sx={{
                    p: 0,
                    '&:hover': {
                      backgroundColor: 'transparent',
                      color: '#ffffff',
                      textDecoration: 'underline',
                    },
                  }}
                >
                  <ListItemText
                    primary={item.label}
                    sx={{
                      '& .MuiTypography-root': {
                        fontSize: '1rem',
                        lineHeight: 1.3,
                      },
                      '&:hover': {
                        color: '#ffffff',
                      },
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Collapse>
      </Box>
    </ClickAwayListener>
  );
};

export default DropdownMenuItem;
